import { AppContext } from "../../../../App";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { EOWException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { IAuthManager } from "../../../../OW.Remoting/Remoting/Auth/IAuthManager";
import { IChangePasswordParams } from "../../../../OW.Remoting/Remoting/Clients/api/auth/types";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IPostInitialize } from "../../../UI.ReactControlConnectors/types";
import { FormControlParams, IControl } from "../../UI/Controls/ControlInterfaces";
import { IContentBuilder } from "../../UI/DataBuilders/IContentBuilder";
import { LocalizedStringBuilder } from "../../UI/DataBuilders/LocalizedStringBuilder";
import { PasswordChangeBuilder } from "../../UI/DataBuilders/PasswordChangeBuilder";
import { MessageWindow } from "../../UI/MessageWindow";
import { UIManager } from "../../UI/UIManager";
import { FormControl } from "../../UI/XGControls/FormControl";
import { PasswordSectionHandler } from "./PasswordSectionHandler";
import { SectionHandler } from "./SectionHandler";

export class ChangePasswordSettingsContainer extends FormControl implements IDisposable, IControl, IPostInitialize {
    private _sections: SectionHandler[];
    private _okButton: HTMLElement;
    private _passwordSectionEl: HTMLElement;
    private _authManager: IAuthManager;
    private _changePwdBuilder: PasswordChangeBuilder;

    constructor(params: FormControlParams) {
        super(params);
        this._authManager = DIContainer.instance.create<IAuthManager>(IAuthManager);
    }

    public async postInitialize(): Promise<void> {
        if (!this._passwordSectionEl) {
            await this._buildSections();
            this._registerListeners();
            this._registerSections();
        }
    }

    private _registerSections(): void {
        UIManager.instance.showLoader();
        try {
            this._sections = [];

            this._sections.push(new PasswordSectionHandler(
                this._passwordSectionEl,
                () => null,
                (_obj) => this._runChange(),
                (content) => this._showInlineMessage(content),
                "pwd-input", "pwd-input-new", "pwd-input-repeat",
            ));

            this._sections.forEach(s => s.changed.add(() => this._okButton.classList.toggle("Disabled", !this._sections.some(s => s.isChanged))));
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _registerListeners(): void {
        this._okButton = this.parentElement.querySelector("[data-id='ok-button-pwd']");
        if (this._okButton) {
            this._okButton.classList.add("Disabled");
            this._events.addListener(this._okButton, "click", async () => {
                if (this._okButton.classList.contains("Disabled"))
                    return;

                UIManager.instance.showLoader();
                try {
                    if (!(await this.save()))
                        return;
                    this._okButton.classList.add("Disabled");
                } finally {
                    UIManager.instance.hideLoader();
                }
            });
        }
    }

    public async save(): Promise<boolean> {
        return (await Promise.all(this._sections.map(s => s.save()))).every(succeeded => succeeded);
    }

    private async _buildSections(): Promise<void> {
        /*
        RegisterSection  v okně uživatelských nastavení pracuje s existující HTML stránkou (application-settings.html), která má elementy jako "#section-container" předdefinované
        a jen je oživuje. Pro custom komponentu je potřeba programově vytvořit. Metoda vezme `this.element` - kořenový element téhle komponenty
        a přes appendChild si vytvoří html strukturu, kterou pak registersections oživuje.
        */
        const settingContainer = createElement({ classes: "change-password-settings-container" });
        const wrp = createElement({ classes: "wrapper" });

        const rolesSection = createElement({ type: "section", html: "<h3><span class=\"icon user-lock\" > </span><span class=\"loc-loading\" data-lkd=\"GUI-10792\" data-lkta=\"tc\"></span > </h3>" });
        this._passwordSectionEl = createElement({ id: "password-change-container" });
        this._changePwdBuilder = new PasswordChangeBuilder();
        this._passwordSectionEl.appendChild(await this._changePwdBuilder.build());

        rolesSection.appendChild(this._passwordSectionEl);
        wrp.appendChild(rolesSection);

        const btn = createElement({ classes: "bottom-buttons" });
        btn.appendChild(createElement({ type: "button", classes: "primary btn btn-large loc-loading", attrs: { "data-id": "ok-button-pwd", "data-lkd": "GUI-678727", "data-lkta": "tc" } })); //Změnit heslo
        wrp.appendChild(btn);

        settingContainer.appendChild(wrp);

        this.element.appendChild(settingContainer);
    }

    private async _showInlineMessage(content: IContentBuilder | null): Promise<void> {
        await this._changePwdBuilder.setInvalidContent(content);
    }

    private async _runChange(): Promise<void> {
        const changePasswordParams: IChangePasswordParams = {
            userName: AppContext.instance.currentUser.userName,
            oldPassword: this._changePwdBuilder.oldPassword,
            newPassword: this._changePwdBuilder.password,
            language: null,
        };
        if (!changePasswordParams.newPassword) //pokud neprošla validace, je v parametru hesla nastaveno null - nepokračujeme
            return;
        const result = await this._authManager.changePassword(changePasswordParams);
        if (!result.status)
            throw new EOWException(result.error.message); //chybu ze serveru zobrazíme přes zachycení výjimky

        this._changePwdBuilder.clear();
        await MessageWindow.showAlert(new LocalizedStringBuilder("SYS-15478")); //Heslo bylo změněno.
    }

    /**
     * Funkce vrací HTML prvek reprezentující tuto třídu.
     **/
    protected _getMainContainerElement(): HTMLElement {
        return document.querySelector("change-password-settings-container");
    }
}
